import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'hooks/redux';
import { Route, useHistory, Redirect } from 'react-router-dom';
import jwt_decode from 'jwt-decode';
import { get } from 'lodash';
import Loader from '../components/loader';
import Spinner from '../components/spinner';
import { USER_STATUSES, SIGNUP_PATHS } from '../constants';
import SidebarContainer from '../containers/sidebar/sidebar-container';
import AppPage from '../modules/app-page/app-page';
import AppPageMain from '../modules/app-page/app-page--main';
import Btn from '../modules/btn';
import Modal from '../modules/modal';
import { Assets } from '../pages/assets';
import AssetInfoPage from '../pages/assets/asset-info';
import AssetSellingInfoPage from '../pages/assets/asset-selling-info';
import AssetsBuyingPage from '../pages/assets/assets-buying/assets-buying';
import AssetsInfoPage from '../pages/assets/assets-info';
import AssetsResultPage from '../pages/assets/assets-result/assets-result';
import AssetsSellingPage from '../pages/assets/assets-selling';
import AssetsSellingInfoPage from '../pages/assets/assets-selling-info';
import AssetsSellingResultPage from '../pages/assets/assets-selling-result';
import AssetsSigningPage from '../pages/assets/assets-signing/assets-signing';
import AssetsSellingSigningPage from '../pages/assets/assets-signing-sale';
import CfaReleasePage from '../pages/assets/cfa-release';
import CfaInfoPage from '../pages/cfa/cfa-info';
import CfaResultPage from '../pages/cfa/cfa-result/cfa-result';
import CfaSign from '../pages/cfa/cfa-sign/cfa-sign';
import CreditInitPage from '../pages/credit/credit-init/credit-init';
import CreditResultPage from '../pages/credit/credit-result/credit-result';
import CreditSignPage from '../pages/credit/credit-sign/credit-sign';
import CreditDetailsPage from '../pages/credit-details/credit-details';
import CreditDetailsRepaymentPage from '../pages/credit-details/credit-details-repayment';
import CreditRepaymentResultPage from '../pages/credit-details/credit-repayment-result';
import DocumentDetailedPage from '../pages/document-details';
import Documents from '../pages/documents';
import EmptyChapter from '../pages/empty';
import {
  Exchange,
  ExchangeInfo,
  ExchangeSign,
  Exchanges,
} from '../pages/exchange';
import {
  ExternalExchange,
  ExternalExchangeInfo,
  ExternalExchangeSign,
} from '../pages/external-exchange';
import TransactionHistory from '../pages/history';
import HybridInfoPage from '../pages/hybrid-info';
import Main from '../pages/main';
import OrderDetailedPage from '../pages/order-detailed-page/order-detailed-page';
import OrderRealizationsPage from '../pages/order-realizations/order-realizations';
import OrderSignResultPage from '../pages/order-sign-result/order-sign-result';
import OrderSelectPaymentPage from '../pages/order-select-payment';
import Orders from '../pages/orders/orders';
import FinalPage from '../pages/final-page';
import { ProfileCompany, ProfileUser } from '../pages/profile';
import SignProcessPage from '../pages/sign-process/sign-process';
import TokenDetailsPage from '../pages/TokenDetailsPage';
import TransactionDetails from '../pages/transaction-details';
import { useAppDispatch } from '../store';
import { fetchActionsInfo } from '../store/actions/actions';
import {
  getProfileInfo,
  refreshToken,
  removeAuthData,
} from '../store/actions/auth';
import { fetchBalanceInfo } from '../store/actions/balance';
import { getCompanies, getCompanyInfo } from '../store/actions/company-info';
import {
  getAvailablePaymentTokens,
  getPaymentTokens,
  getPaymentTypes,
} from '../store/actions/common-info';
import { RootState } from '../types';
import { configureAxios } from '../utils/configureAxios';
import { getTimer } from '../utils/strings/getTimer';
import { getUserStatus } from '../utils/utils';
import OperatorRestoreKeyPage from 'pages/OperatorRestoreKeyPage';
import CompanyVerificationPage from 'pages/CompanyVerificationPage';
import CreateOperationPage from 'pages/CreateOperationPage';
import AddPermissionsPage from 'pages/AddPermissionsPage';
import SignerCardPage from 'pages/SignerCardPage';

const AuthPaths = () => {
  const [isOpenRefresh, setOpenRefresh] = useState(false);
  const dispatch = useAppDispatch();
  const history = useHistory();
  const token = useSelector((state: RootState) => state.auth.account.token);
  const [timer, setTimer] = useState(60);
  let timeout: number | null;
  const balanseTimerRef = useRef<number | null>(null);
  let interval: number | null;

  const clearToken = () => {
    interval && window.clearInterval(interval);
    interval = null;
    if (balanseTimerRef.current) {
      window.clearTimeout(balanseTimerRef.current);
      balanseTimerRef.current = null;
    }
    dispatch(removeAuthData({}));
    history.push('/auth');
    return undefined;
  };

  const handleRefreshToken = () => {
    dispatch(refreshToken()).then(() => {
      interval && window.clearInterval(interval);
      interval = null;
      timeout && window.clearInterval(timeout);
      timeout = null;
    });
  };

  const validateToken = () => {
    const theToken = token;
    if (!theToken) {
      return clearToken();
    }

    const userStatus = getUserStatus(theToken);
    if (userStatus !== USER_STATUSES.ACTIVE) {
      return undefined;
    }

    const expTime: number = get(jwt_decode(theToken), 'exp', 0);
    const unixTimestamp = Math.round(Date.now() / 1000);

    if (expTime && unixTimestamp > expTime - 119 && unixTimestamp < expTime) {
      !timer && setTimer(60);
      setOpenRefresh(true);
    }

    if (expTime && unixTimestamp < expTime) {
      if (!interval) {
        interval = window.setInterval(() => {
          validateToken();
        }, 1000 * 60);
      }

      return theToken;
    }

    return clearToken();
  };

  useEffect(() => {
    balanseTimerRef.current = window.setTimeout(function getBalanseTimer() {
      dispatch(fetchBalanceInfo()).then(() => {
        balanseTimerRef.current = window.setTimeout(getBalanseTimer, 3000);
      });
    }, 3000);

    return () => {
      if (balanseTimerRef.current) {
        window.clearTimeout(balanseTimerRef.current);
        balanseTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const tokenString = validateToken();
    if (tokenString) {
      configureAxios(tokenString);
      dispatch(fetchActionsInfo());
      dispatch(getCompanyInfo()).then((res: any) => {
        let payload = res.payload as {
          status: 'VERIFIED' | 'NON_VERIFIED' | 'NEED_KEY_GENERATION';
        };
        if (payload?.status === 'NEED_KEY_GENERATION') {
          history.push(SIGNUP_PATHS.KEY_GENERATION);
        } else if (payload?.status === 'NON_VERIFIED') {
          history.push(SIGNUP_PATHS.VERIFICATION);
        }
      });
      dispatch(getPaymentTypes());
      dispatch(getPaymentTokens());
      dispatch(getAvailablePaymentTokens());
      dispatch(getCompanies());
      dispatch(getProfileInfo());
    } else {
      history.push('/auth');
    }

    return () => {
      interval && window.clearInterval(interval);
      interval = null;
    };
  }, [token]);

  useEffect(() => {
    let time = timer;
    if (isOpenRefresh) {
      timeout = window.setInterval(() => {
        time -= 1;
        if (time >= 0) setTimer(time);
      }, 1000);
    }
  }, [isOpenRefresh]);

  useEffect(() => {
    if (timer < 1 && isOpenRefresh) {
      timeout && window.clearInterval(timeout);
      timeout = null;
      setOpenRefresh(false);
      clearToken();
    }
  }, [isOpenRefresh, timer]);

  const position = useSelector((state) => state.auth.profile.position);

  return (
    <AppPage>
      {!token || !position ? (
        <Spinner />
      ) : (
        <>
          <SidebarContainer />
          <AppPageMain>
            {position === 'OPERATOR' && (
              <>
                <Route
                  path="/"
                  exact
                  component={() => <Redirect to="/assets" />}
                />
                <Route
                  path="/operator/restore-key/:id"
                  exact
                  component={OperatorRestoreKeyPage}
                />
                <Route
                  path="/operator/company-verification/:id"
                  exact
                  component={CompanyVerificationPage}
                />
                <Route
                  path="/operator/create-operation"
                  exact
                  component={CreateOperationPage}
                />
                <Route
                  path="/operator/add-signer-permissions"
                  exact
                  component={AddPermissionsPage}
                />
                <Route
                  path="/operator/signer-card/:id"
                  exact
                  component={SignerCardPage}
                />
              </>
            )}
            <Route path="/assets" exact component={Assets} />
            <Route path="/history" exact component={TransactionHistory} />
            <Route path="/profile/company" exact component={ProfileCompany} />
            <Route path="/profile/user" exact component={ProfileUser} />
            <Route path="/assets/details/:id" component={TokenDetailsPage} />
            {position !== 'OPERATOR' && (
              <>
                <Route path="/" exact component={Main} />
                <Route path="/orders" exact component={Orders} />
                <Route path="/documents" exact component={Documents} />
                <Route path="/agents" exact component={EmptyChapter} />
                <Route
                  path="/external-exchange"
                  exact
                  component={ExternalExchange}
                />
                <Route
                  path="/external-exchange-sign"
                  exact
                  component={ExternalExchangeSign}
                />
                <Route
                  path="/external-exchange-info/:id"
                  exact
                  component={ExternalExchangeInfo}
                />
                <Route path="/exchanges" exact component={Exchanges} />
                <Route path="/exchange" exact component={Exchange} />
                <Route path="/exchange-sign" exact component={ExchangeSign} />
                <Route
                  path="/exchange-info/:id"
                  exact
                  component={ExchangeInfo}
                />
                <Route path="/orders/:id" exact component={OrderDetailedPage} />
                <Route
                  path="/orders/:id/selectPayment"
                  exact
                  component={OrderSelectPaymentPage}
                />
                <Route
                  exact
                  path="/orders/:id/items/:itemId/realizations"
                  component={OrderRealizationsPage}
                />
                <Route
                  path="/orders/:id/realizations/sign"
                  component={SignProcessPage}
                />
                <Route
                  path="/orders/:id/sign/result"
                  component={OrderSignResultPage}
                />
                <Route path="/credit/init" component={CreditInitPage} />
                <Route path="/credit/sign" component={CreditSignPage} />
                <Route
                  path="/credit/sign-result"
                  component={CreditResultPage}
                />
                <Route
                  exact
                  path="/credit/details/:id"
                  component={CreditDetailsPage}
                />
                <Route
                  path="/credit/details/:id/repayment"
                  component={CreditDetailsRepaymentPage}
                />
                <Route
                  path="/credit/repayment-result"
                  component={CreditRepaymentResultPage}
                />
                <Route path="/assets/buying" component={AssetsBuyingPage} />
                <Route path="/assets/signing" component={AssetsSigningPage} />
                <Route
                  path="/assets/sign-result"
                  component={AssetsResultPage}
                />
                <Route
                  path="/assets/sign-info/:id?"
                  exact
                  component={AssetsInfoPage}
                />
                <Route path="/assets/selling" component={AssetsSellingPage} />
                <Route
                  path="/assets/signing-sale"
                  component={AssetsSellingSigningPage}
                />
                <Route
                  path="/assets/selling-sign-result"
                  component={AssetsSellingResultPage}
                />
                <Route
                  exact
                  path="/assets/selling-sign-info/:id?"
                  component={AssetsSellingInfoPage}
                />
                <Route path="/assets/cfa-info/:id" component={CfaInfoPage} />
                <Route path="/assets/cfa-release" component={CfaReleasePage} />
                <Route path="/cfa/sign" component={CfaSign} />
                <Route path="/cfa/sign-result" component={CfaResultPage} />
                <Route
                  exact
                  path="/history/selling-sign-info/:id"
                  component={AssetSellingInfoPage}
                />
                <Route
                  exact
                  path="/history/sign-info/:id"
                  component={AssetInfoPage}
                />
                <Route path="/history/cfa-info/:id" component={CfaInfoPage} />
                <Route
                  path="/history/hybrid-info/:id"
                  component={HybridInfoPage}
                />
                <Route
                  path="/history/exchange-info/:id"
                  exact
                  component={ExchangeInfo}
                />
                <Route
                  path="/history/details/:id"
                  exact
                  component={TransactionDetails}
                />
                <Route
                  path="/documents/:id"
                  exact
                  component={DocumentDetailedPage}
                />
                <Route path="/assets/result" exact component={FinalPage} />
              </>
            )}
          </AppPageMain>
          <Modal
            open={isOpenRefresh}
            header={
              <div className="flex justify-between">
                <h4>Внимание!</h4>
                <p className="timer">{getTimer(timer)}</p>
              </div>
            }
            className="refresh-modal"
            body="Через минуту ваша сессия истечёт. Если Вы не хотите выходить - нажмите ПРОДОЛЖИТЬ!"
            footer={{
              left: (
                <Btn
                  text="Отмена"
                  buttonType="text"
                  onClick={() => clearToken()}
                />
              ),
              right: (
                <Btn
                  text="Продолжить"
                  buttonType="submit"
                  onClick={() => {
                    handleRefreshToken();
                    setOpenRefresh(false);
                  }}
                />
              ),
            }}
            onClose={() => clearToken()}
          />
          <Loader />
        </>
      )}
    </AppPage>
  );
};

export default AuthPaths;
